import { useCallback, useState } from 'react';
import { useAppStorageFiles } from '@workspace/ui/hooks/useAppStorage';
import type { ChatSession } from '../types';
import {
  planExport,
  buildConversationZip,
  exportFileName,
  sessionStoragePrefix,
  type ExportManifest,
  type ZipBuilder,
} from '../export/conversation-export';

const APP_NAME = 'chat';

export interface ExportState {
  exporting: boolean;
  /** Set when the archive was built but not everything made it in. */
  warning: string | null;
}

/**
 * Download a conversation as a zip (tdd-0076 Phase 6).
 *
 * jszip is imported LAZILY. It is ~100KB and only needed when someone actually
 * exports — putting it in the entry chunk would tax every session that never
 * does, which is nearly all of them.
 */
export function useConversationExport() {
  const { download, listPrefix } = useAppStorageFiles(APP_NAME);
  const [state, setState] = useState<ExportState>({ exporting: false, warning: null });

  const exportConversation = useCallback(
    async (session: ChatSession) => {
      setState({ exporting: true, warning: null });

      try {
        // What the conversation's prefix ACTUALLY holds, rather than only what
        // the transcript references — an artifact whose tool result never made
        // it back would otherwise be silently dropped from the export.
        let items: Array<{
          key: string;
          size?: number;
          contentType?: string;
          displayName?: string;
        }> = [];
        try {
          const listed = await listPrefix(sessionStoragePrefix(session.id));
          items = listed.map((i) => ({
            key: i.key,
            size: i.size,
            contentType: i.contentType,
            // The name the user gave it, recorded at upload precisely so the
            // export can restore it over the uuid in the key.
            displayName: i.metadata?.displayName,
          }));
        } catch (error) {
          // A listing failure degrades to a transcript-only export rather than
          // no export. Reported, never silent.
          console.error('[export] could not list artifacts:', error);
        }

        const plan = planExport(session, items.filter((i) => i.key), new Date().toISOString());

        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();
        const builder: ZipBuilder = {
          file: (path, data) => void zip.file(path, data),
          generate: () => zip.generateAsync({ type: 'blob' }),
        };

        const { blob, manifest } = await buildConversationZip(
          plan,
          async (key) => download(key),
          builder,
        );

        triggerDownload(blob, exportFileName(session, new Date()));
        setState({ exporting: false, warning: warningFor(manifest, items.length === 0) });
      } catch (error) {
        console.error('[export] failed:', error);
        setState({
          exporting: false,
          warning: 'The export could not be completed.',
        });
      }
    },
    [download, listPrefix],
  );

  const dismissWarning = useCallback(
    () => setState((s) => ({ ...s, warning: null })),
    [],
  );

  return { ...state, exportConversation, dismissWarning };
}

/**
 * What to tell the user about an archive that is not complete.
 *
 * Returning null for a clean export matters as much as the message: a warning
 * shown every time is one nobody reads when it counts.
 */
function warningFor(manifest: ExportManifest, listingFailed: boolean): string | null {
  if (listingFailed) {
    return 'Saved files could not be listed, so the export contains the transcript only.';
  }
  if (manifest.omitted.length > 0) {
    const n = manifest.omitted.length;
    return `Exported, but ${n} file${n === 1 ? '' : 's'} could not be included. See manifest.json for details.`;
  }
  return null;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
